/* eslint-disable jsx-a11y/anchor-is-valid */
import { useIsVisible } from '@folklore/hooks';
import { getComponentFromName } from '@folklore/utils';
import classNames from 'classnames';
import React, { useEffect, ReactNode } from 'react';

import useDocuments from '../../hooks/useDocuments';
import usePlaceholderItems from '../../hooks/usePlaceholderItems';
// import * as AppPropTypes from '../../lib/PropTypes';
import { getPrefixedProps } from '../../lib/utils';

import { useIsCapture } from '../../contexts/LayoutContext';
import * as CardComponents from '../cards/documents';
import GridList from '../lists/GridList';

import styles from '../../styles/headers/featured-sidebar-header.module.css';

export interface FeaturedSidebarHeaderProps {
    header?: ReactNode;
    title?: ReactNode;
    brand?: Brand;
    count?: number | null;
    sidebarCount?: number;
    query?: Record<string, unknown> | null;
    items?: unknown[] | null;
    loading?: string | null;
    featuredCardType?: string | null;
    featuredCardTheme?: CardTheme | LegacyCardTheme | null;
    featuredCardRatio?: string | null;
    featuredCardWith?: string[] | null;
    featuredCardWithout?: string[] | null;
    featuredCardLinkQuery?: Record<string, unknown> | null;
    cardType?: string | null;
    cardTheme?: CardTheme | LegacyCardTheme | null;
    cardRatio?: string | null;
    cardWith?: string[] | null;
    cardWithout?: string[] | null;
    cardImageLoading?: 'lazy' | null | undefined;
    sidebar?: ReactNode;
    withoutLoader?: boolean;
    withShadow?: boolean;
    responsive?: boolean;
    className?: string | null;
    mainClassName?: string | null;
    titleClassName?: string | null;
    sidebarClassName?: string | null;
    featuredCardClassName?: string | null;
    featuredLabelClassName?: string | null;
    featuredImageContainerClassName?: string | null;
    onLoaded?: ((items: unknown[]) => void) | null;
    cardWidth?: number;
    autoColumns?: boolean;
}

const FeaturedSidebarHeader = ({
    header = null,
    title = null,
    brand = null,
    count = null,
    sidebarCount = 6,
    query = null,
    items: providedItems = null,
    loading = null,
    featuredCardType = 'featured',
    featuredCardTheme = 'over-bottom-left',
    featuredCardRatio = undefined,
    featuredCardWith = null,
    featuredCardWithout = null,
    featuredCardLinkQuery = null,
    cardType = null,
    cardTheme = 'box',
    cardRatio = null,
    cardWith = null,
    cardWidth = 150,
    autoColumns = false,
    cardWithout = null,
    cardImageLoading = undefined,
    sidebar = null,
    withoutLoader = false,
    withShadow = false,
    responsive = false,
    className = null,
    mainClassName = null,
    titleClassName = null,
    sidebarClassName = null,
    featuredCardClassName = null,
    featuredLabelClassName = null,
    featuredImageContainerClassName = null,
    onLoaded = null,
}: FeaturedSidebarHeaderProps) => {
    const isCapture = useIsCapture();
    const isLazyLoading = loading === 'lazy' && !isCapture;
    const { ref: visibleRef, visible: isVisible } = useIsVisible({
        persist: true,
        disabled: !isLazyLoading,
    });
    const shouldLoad = !isLazyLoading || isVisible;
    const finalCount = sidebar !== null ? 1 : count || 1 + sidebarCount;
    const finalSidebarCount = finalCount - 1;
    const { handle: brandHandle = null } = brand || {};
    const { items: loadedItems } = useDocuments(
        {
            count: finalCount,
            ...query,
        },
        {
            enabled: !withoutLoader && shouldLoad,
            keepPreviousData: false,
        },
    );
    // The query returns null items when it does not load (withoutLoader)
    const items = loadedItems !== null ? loadedItems : providedItems;
    const [featuredItem = null, ...otherItems] = items || [];
    const placeholderItems = usePlaceholderItems(finalSidebarCount);

    const FeaturedCard = getComponentFromName(
        CardComponents,
        featuredCardType,
        CardComponents.Featured,
    );

    useEffect(() => {
        if (items !== null && onLoaded !== null) {
            onLoaded(items);
        }
    }, [items]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles[brandHandle]]: brandHandle !== null,
                    [styles.responsive]: responsive,
                    [styles.withShadow]: withShadow,
                    [styles.oneColumn]: otherItems.length < 4,
                    [className]: className !== null,
                },
            ])}
            ref={visibleRef}
        >
            {header ||
                (title !== null ? (
                    <div className={classNames([styles.title, titleClassName])}>{title}</div>
                ) : null)}
            <div className={styles.inner}>
                <div className={classNames([styles.main, mainClassName])}>
                    <FeaturedCard
                        {...featuredItem}
                        {...getPrefixedProps(featuredCardWithout || cardWithout, 'without')}
                        {...getPrefixedProps(featuredCardWith || cardWith, 'with')}
                        linkQuery={featuredCardLinkQuery}
                        placeholder={featuredItem === null}
                        // placeholder
                        theme={featuredCardTheme}
                        ratio={featuredCardRatio}
                        // responsive={responsive}
                        className={classNames([
                            styles.featuredCard,
                            {
                                [styles.isOver]:
                                    featuredCardTheme !== null &&
                                    featuredCardTheme.indexOf('over') === 0,
                                [styles.withRatio]: featuredCardRatio !== null,
                            },
                            featuredCardClassName,
                        ])}
                        innerClassName={styles.featuredInner}
                        labelClassName={classNames([styles.featuredLabel, featuredLabelClassName])}
                        imageContainerClassName={classNames([
                            styles.featuredImageContainer,
                            featuredImageContainerClassName,
                        ])}
                        imageClassName={styles.featuredImage}
                        imageLoading={cardImageLoading}
                        withBreakpoints
                        withBorderRadius
                    />
                </div>
                <aside className={classNames([styles.sidebar, sidebarClassName])}>
                    {sidebar || (
                        <GridList
                            items={
                                otherItems.length < finalSidebarCount
                                    ? [
                                          ...otherItems,
                                          //   ...placeholderItems.slice(
                                          //       0,
                                          //       finalSidebarCount - otherItems.length,
                                          //   ),
                                      ]
                                    : otherItems
                            }
                            cardWidth={cardWidth}
                            autoColumns={autoColumns}
                            cardComponent={getComponentFromName(
                                CardComponents,
                                cardType,
                                CardComponents.Vertical,
                            )}
                            cardTheme={cardTheme}
                            cardRatio={cardRatio}
                            cardSize="medium"
                            cardWith={cardWith}
                            cardWithout={cardWithout}
                            cardResponsive={responsive}
                            cardImageLoading={cardImageLoading}
                            className={classNames([
                                styles.list,
                                {
                                    [styles.grid]: true,
                                },
                            ])}
                            itemsClassName={styles.items}
                            itemClassName={styles.item}
                            cardClassName={styles.card}
                        />
                    )}
                </aside>
            </div>
        </div>
    );
};

export default FeaturedSidebarHeader;
